import {
  useQuery,
  type UseQueryOptions,
  type QueryClient,
} from '@tanstack/react-query';
import { useIdentify } from '@hungryroot/customer';
import { StatusCode } from '@hungryroot/rest';
import {
  type Params as GetApiV2CustomersCustomerIdRatableProductsFeedbackModalParams,
  getApiV2CustomersCustomerIdRatableProductsFeedbackModal,
  type CustomerRatableProduct as RatableProduct,
} from '@hungryroot/rest/api/v2/customers/[id]/ratable_products_feedback_modal/get';

export { RatableProduct };

const getRatableProductsFeedbackModalQueryKey = ({
  id,
  queryParams,
}: {
  id: number;
  queryParams?: QueryParams;
}) => {
  const returnedKey = ['Products', 'ratable products feedback modal', id];

  if (queryParams) {
    return [...returnedKey, queryParams];
  }

  return returnedKey;
};

interface InvalidateRatableProductsFeedbackModalArgs {
  client: QueryClient;
  customerId: number;
  queryParams?: QueryParams;
}

export function invalidateRatableProductsFeedbackModal({
  client,
  customerId,
  queryParams,
}: InvalidateRatableProductsFeedbackModalArgs) {
  client.invalidateQueries({
    queryKey: getRatableProductsFeedbackModalQueryKey({
      id: customerId,
      queryParams,
    }),
  });
}

type QueryParams = Omit<
  GetApiV2CustomersCustomerIdRatableProductsFeedbackModalParams,
  'customerId'
>;

type UseRatableProductsFeedbackModalProps = {
  id: number;
  options?: Pick<UseQueryOptions, 'enabled'>;
  queryParams?: QueryParams;
};

function useRatableProductsFeedbackModal(
  params: UseRatableProductsFeedbackModalProps
) {
  const { id, options, queryParams } = params;
  return useQuery({
    queryKey: getRatableProductsFeedbackModalQueryKey({ id, queryParams }),
    queryFn: async () => {
      const resp =
        await getApiV2CustomersCustomerIdRatableProductsFeedbackModal({
          customerId: id,
          ...queryParams,
        });

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    enabled: options?.enabled === false || id === 0 ? false : true,
  });
}

export function useSelfRatableProductsFeedbackModal(
  params?: Omit<UseRatableProductsFeedbackModalProps, 'id'>
) {
  const { customerData } = useIdentify();

  return useRatableProductsFeedbackModal({
    ...params,
    id: customerData?.id ?? 0,
  });
}
